/**
 * Date / time helpers for the Procedures API conventions:
 *   - stamps arrive as `yyyyMMddHHmmss` numbers, localised to the caller at the JSON boundary
 *   - request stamps we send (clientCheckedAt) are UTC `yyyyMMddHHmmss` (UTC at rest)
 * Everything renders in plain South African English ("Sun 25 Oct, 08:12", "12 min").
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

/** `yyyyMMddHHmmss` → local Date (the API already localised it). */
export function stampToDate(stamp: number | string | null | undefined): Date | null {
  if (stamp === null || stamp === undefined || stamp === '' || stamp === 0) return null;
  const s = String(Math.trunc(Number(stamp))).padStart(14, '0');
  if (s.length !== 14) return null;
  const d = new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14)),
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Now (or `at`) as a UTC `yyyyMMddHHmmss` number, for clientCheckedAt on queued ticks. */
export function utcStamp(at: Date | number = new Date()): number {
  const d = typeof at === 'number' ? new Date(at) : at;
  const s =
    `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}` +
    `${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}`;
  return Number(s);
}

/** "Sun 25 Oct" */
export function formatShortDate(value: Date | null | undefined): string {
  if (!value) return '';
  return `${DAYS[value.getDay()].slice(0, 3)} ${value.getDate()} ${MONTHS[value.getMonth()].slice(0, 3)}`;
}

/** "08:12" */
export function formatClock(value: Date | null | undefined): string {
  if (!value) return '';
  return `${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
}

/** "Sun 25 Oct, 08:12" for API stamps. */
export function formatStamp(stamp: number | null | undefined): string {
  const d = stampToDate(stamp);
  if (!d) return '';
  return `${formatShortDate(d)}, ${formatClock(d)}`;
}

/** "Today, 08:12" / "Sun 25 Oct, 08:12" */
export function formatWhen(d: Date | null | undefined): string {
  if (!d) return '';
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? `Today, ${formatClock(d)}` : `${formatShortDate(d)}, ${formatClock(d)}`;
}

/** 42 → "under a minute", 600 → "10 min", 4500 → "1 h 15 min". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return 'under a minute';
  const totalMin = Math.round(seconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "About 10 minutes" for estimates. */
export function formatEstimate(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `About ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return `About ${formatDuration(minutes * 60)}`;
}

/** Initials for an avatar ("Grace Molefe" → "GM"). */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';
}

/** "1 step" / "3 steps" */
export function plural(n: number, singular: string, pluralForm?: string): string {
  return `${n} ${n === 1 ? singular : pluralForm ?? `${singular}s`}`;
}
